import { useState, useEffect } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Badge } from '../../components/ui/badge';
import { Plus, Pencil, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import ImageUpload from '../../components/ImageUpload';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const emptyPost = {
  title_de: '', title_en: '', title_es: '',
  content_de: '', content_en: '', content_es: '',
  excerpt_de: '', excerpt_en: '', excerpt_es: '',
  image: '', author: 'ITCS Team', published: false,
};

export default function AdminBlog() {
  const [items, setItems] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ ...emptyPost });
  const token = localStorage.getItem('itcs-admin-token');
  const headers = { Authorization: `Bearer ${token}` };

  const load = () => axios.get(`${API}/admin/blog`, { headers }).then(r => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm({ ...emptyPost }); setEditing(null); setDialogOpen(true); };
  const openEdit = (item) => { setForm({ ...item }); setEditing(item.id); setDialogOpen(true); };
  const updateForm = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const handleSave = async () => {
    try {
      if (editing) {
        await axios.put(`${API}/admin/blog/${editing}`, form, { headers });
        toast.success('Beitrag aktualisiert');
      } else {
        await axios.post(`${API}/admin/blog`, form, { headers });
        toast.success('Beitrag erstellt');
      }
      setDialogOpen(false); load();
    } catch { toast.error('Fehler beim Speichern'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Beitrag wirklich löschen?')) return;
    try { await axios.delete(`${API}/admin/blog/${id}`, { headers }); toast.success('Gelöscht'); load(); }
    catch { toast.error('Fehler'); }
  };

  return (
    <div data-testid="admin-blog">
      <div className="flex items-center justify-between mb-8">
        <h1 className="text-2xl font-bold text-[#0F172A]">Blog / News verwalten</h1>
        <Button onClick={openNew} className="bg-[#066ce7] hover:bg-[#005bb5] text-white" data-testid="add-blog-btn"><Plus className="w-4 h-4 mr-2" /> Neuer Beitrag</Button>
      </div>
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <Table>
          <TableHeader><TableRow><TableHead>Titel (DE)</TableHead><TableHead>Autor</TableHead><TableHead>Datum</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Aktionen</TableHead></TableRow></TableHeader>
          <TableBody>
            {items.map(p => (
              <TableRow key={p.id}>
                <TableCell className="font-medium max-w-xs truncate">{p.title_de}</TableCell>
                <TableCell className="text-sm text-slate-500">{p.author}</TableCell>
                <TableCell className="text-sm text-slate-500">{p.created_at ? new Date(p.created_at).toLocaleDateString('de-DE') : ''}</TableCell>
                <TableCell>{p.published ? <Badge className="bg-emerald-100 text-emerald-700">Veröffentlicht</Badge> : <Badge variant="outline">Entwurf</Badge>}</TableCell>
                <TableCell className="text-right space-x-2">
                  <Button variant="ghost" size="sm" onClick={() => openEdit(p)}><Pencil className="w-4 h-4" /></Button>
                  <Button variant="ghost" size="sm" onClick={() => handleDelete(p.id)} className="text-red-500"><Trash2 className="w-4 h-4" /></Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? 'Beitrag bearbeiten' : 'Neuer Beitrag'}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-3">
              <div><Label className="text-xs">Titel DE</Label><Input value={form.title_de} onChange={e => updateForm('title_de', e.target.value)} /></div>
              <div><Label className="text-xs">Titel EN</Label><Input value={form.title_en} onChange={e => updateForm('title_en', e.target.value)} /></div>
              <div><Label className="text-xs">Titel ES</Label><Input value={form.title_es} onChange={e => updateForm('title_es', e.target.value)} /></div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label className="text-xs">Auszug DE</Label><Textarea value={form.excerpt_de} onChange={e => updateForm('excerpt_de', e.target.value)} rows={2} /></div>
              <div><Label className="text-xs">Auszug EN</Label><Textarea value={form.excerpt_en} onChange={e => updateForm('excerpt_en', e.target.value)} rows={2} /></div>
              <div><Label className="text-xs">Auszug ES</Label><Textarea value={form.excerpt_es} onChange={e => updateForm('excerpt_es', e.target.value)} rows={2} /></div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label className="text-xs">Inhalt DE</Label><Textarea value={form.content_de} onChange={e => updateForm('content_de', e.target.value)} rows={5} /></div>
              <div><Label className="text-xs">Inhalt EN</Label><Textarea value={form.content_en} onChange={e => updateForm('content_en', e.target.value)} rows={5} /></div>
              <div><Label className="text-xs">Inhalt ES</Label><Textarea value={form.content_es} onChange={e => updateForm('content_es', e.target.value)} rows={5} /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Autor</Label><Input value={form.author} onChange={e => updateForm('author', e.target.value)} /></div>
              <div className="flex items-center gap-3 pt-5"><Switch checked={form.published} onCheckedChange={v => updateForm('published', v)} /><Label className="text-xs">Veröffentlicht</Label></div>
            </div>
            <ImageUpload value={form.image} onChange={v => updateForm('image', v)} label="Beitragsbild" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Abbrechen</Button>
            <Button onClick={handleSave} className="bg-[#066ce7] text-white" data-testid="save-blog-btn">Speichern</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
